// 1. Define the type for our UTM parameters
export interface UtmParams {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_term?: string
  utm_content?: string
}

// An array of the keys we care about
const utmKeys: (keyof UtmParams)[] = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
]

// Key for localStorage
const UTM_STORAGE_KEY = 'utm_first_touch'

/**
 * Parses the current URL's query string and extracts UTM parameters.
 * @returns An object containing the UTM parameters.
 */
export function getUtmParams(): UtmParams {
  // Ensure this code only runs in a browser environment
  if (typeof window === 'undefined') {
    return {
      utm_source: null,
      utm_medium: null,
      utm_campaign: null,
      utm_term: null,
      utm_content: null,
    }
  }

  const params = new URLSearchParams(window.location.search)
  const utmData: Partial<UtmParams> = {}

  for (const key of utmKeys) {
    utmData[key] = params.get(key)
  }

  return utmData as UtmParams
}

/**
 * Checks for UTMs in the URL and stores them in localStorage if it's the first time.
 * This function should be called once when your application loads.
 */
export function storeUtmParamsOnce(): void {
  const storedUtms = localStorage.getItem(UTM_STORAGE_KEY)

  // If we already have stored data, do nothing.
  if (storedUtms) {
    return
  }

  const utmsFromUrl = getUtmParams()

  // Check if at least one UTM parameter exists in the URL
  const hasUtmsInUrl = Object.values(utmsFromUrl).some((value) => value !== null)

  // If there are UTMs in the URL and nothing is stored, save them.
  if (hasUtmsInUrl) {
    localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utmsFromUrl))
  }
}

/**
 * Retrieves the stored UTM data when needed (e.g., on a form submission).
 */
export function getStoredUtmParams(): UtmParams | null {
  const storedData = localStorage.getItem(UTM_STORAGE_KEY)

  if (storedData) {
    return JSON.parse(storedData) as UtmParams
  }

  return null
}
